import React, { type FC, memo, type ReactNode, useCallback } from "react";
import { Description, FieldError, Input, InputGroup, Label, Spinner, TextField } from "@heroui/react";
import { type ApolloClient, gql, type TypedDocumentNode } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import { type GQL } from "@binale-tech/shared";
import { getCountrySpecifications, isValidIBAN } from "ibantools";
import { useIntl } from "react-intl";
import { getName } from "i18n-iso-countries";
import { ignoreAbort } from "@domain/graphql/abortErrors";

const validateIbanQuery: TypedDocumentNode<Pick<GQL.IQuery, "validateIban">> = gql`
    query validateIban($iban: String!) {
        validateIban(iban: $iban)
    }
`;

const bankingBankSearchQuery: TypedDocumentNode<
    Pick<GQL.IQuery, "bankingBankSearch">,
    GQL.IQueryBankingBankSearchArgs
> = gql`
    query bankingBankSearch($IBAN: String!) {
        bankingBankSearch(IBAN: $IBAN) {
            id
            bic
            blz
            name
        }
    }
`;

const getIBANCountryCode = (value: string): string => {
    const code = value?.substring(0, 2);
    const spec = code ? getCountrySpecifications()[code] : undefined;
    return spec?.chars ? code : "";
};

type IbanInputProps = {
    value?: string;
    onChange?: (value: string) => void;
    onBlur?: () => void;
    onBicFound?: (data: { bic?: string; bankName?: string }) => void;
    isInvalid?: boolean;
    errorMessage?: ReactNode;
    description?: ReactNode;
    isDisabled?: boolean;
    isValidating?: boolean;
    placeholder?: string;
};

export const validateIban = async (
    client: ApolloClient,
    value: string | undefined,
    isRequired?: boolean,
): Promise<string | true> => {
    if (!value) {
        return isRequired ? "Required field" : true;
    }
    if (!isValidIBAN(value)) {
        return "Invalid IBAN";
    }
    const isValid = await client
        .query({
            query: validateIbanQuery,
            variables: { iban: value },
            fetchPolicy: "no-cache",
        })
        .then((res) => res.data.validateIban)
        .catch(() => false);
    if (!isValid) {
        return "IBAN ist ungültig";
    }
    return true;
};

const formatIbanValue = (v: string): string => v.toUpperCase().replace(/[^A-Z0-9]/g, "");

const InputIBANLabel = ({ countryCode }: { countryCode: string }) => {
    const intl = useIntl();

    return (
        <span>
            <span className="font-semibold truncate">IBAN</span>
            <span className="text-muted" data-testid="country-addon">
                {countryCode && <span className="ml-2 max-w-[100px]">{getName(countryCode, intl.locale)}</span>}
            </span>
        </span>
    );
};

export const IbanInput: FC<IbanInputProps> = memo(
    ({
        value,
        onChange,
        onBlur,
        onBicFound,
        isInvalid,
        errorMessage,
        description,
        isDisabled,
        isValidating,
        placeholder,
    }) => {
        const [searchBank] = useLazyQuery(bankingBankSearchQuery);

        const countryCode = getIBANCountryCode(value || "");
        const countrySpec = getCountrySpecifications()[countryCode];
        const maxLength = countrySpec?.chars || 34;

        const handleChange = useCallback((v: string) => onChange(formatIbanValue(v)), [onChange]);

        const handleBlur = useCallback(() => {
            onBlur?.();
            if (value && isValidIBAN(value) && onBicFound) {
                searchBank({
                    variables: { IBAN: value },
                })
                    .then(({ data }) => {
                        const bics = new Set(data?.bankingBankSearch?.map((v) => v.bic) ?? []);
                        if (bics.size === 1) {
                            const bank = data.bankingBankSearch[0];
                            onBicFound({ bic: bank.bic, bankName: bank.name });
                        }
                    })
                    .catch(ignoreAbort);
            }
        }, [searchBank, value, onBlur, onBicFound]);

        return (
            <TextField
                value={value || ""}
                onChange={handleChange}
                isInvalid={isInvalid}
                isDisabled={isDisabled}
                className="w-100"
            >
                <Label>
                    <InputIBANLabel countryCode={countryCode} />
                </Label>
                <InputGroup>
                    <InputGroup.Input placeholder={placeholder} onBlur={handleBlur} maxLength={maxLength} />
                    <InputGroup.Suffix>{isValidating && <Spinner size="sm" />}</InputGroup.Suffix>
                </InputGroup>
                {description && <Description>{description}</Description>}
                {errorMessage && <FieldError>{errorMessage}</FieldError>}
            </TextField>
        );
    },
);
